import type { GeocodeResult } from '../types';
import type { RoutePoint } from '../types/storage';

/**
 * Utility functions to convert between geocoding data and database storage format
 */

/**
 * Converts a GeocodeResult to database address fields
 * @param geocodeResult The geocoding result from the address resolver
 * @returns Object with address fields for database storage
 */
export const geocodeResultToAddressFields = (geocodeResult: GeocodeResult | undefined) => {
  if (!geocodeResult) {
    return {
      address_street: undefined,
      address_house_number: undefined,
      address_city: undefined,
      address_suburb: undefined,
      address_country: undefined,
      address_display_name: undefined,
      address_raw_data: undefined
    };
  }

  return {
    address_street: geocodeResult.street,
    address_house_number: geocodeResult.houseNumber,
    address_city: geocodeResult.city,
    address_suburb: geocodeResult.suburb,
    address_country: geocodeResult.country,
    address_display_name: geocodeResult.displayName,
    address_raw_data: geocodeResult.rawData
  };
};

/**
 * Converts database address fields back to a GeocodeResult
 * @param routePoint The route point with address fields from database
 * @returns GeocodeResult or undefined if no address data
 */
export const addressFieldsToGeocodeResult = (routePoint: RoutePoint): GeocodeResult | undefined => {
  if (!routePoint.address_display_name) {
    return undefined;
  }

  return {
    street: routePoint.address_street,
    houseNumber: routePoint.address_house_number,
    city: routePoint.address_city,
    suburb: routePoint.address_suburb,
    country: routePoint.address_country,
    displayName: routePoint.address_display_name,
    rawData: routePoint.address_raw_data
  };
};

/**
 * Formats address fields from database into a human-readable string
 * Similar to formatGeocodeResult but works directly with database fields
 * @param routePoint The route point with address fields
 * @returns Formatted address string
 */
export const formatAddressFromRoutePoint = (routePoint: RoutePoint): string | undefined => {
  if (!routePoint.address_display_name) {
    return undefined;
  }

  const parts: string[] = [];

  // Add street and house number
  if (routePoint.address_street) {
    if (routePoint.address_house_number) {
      parts.push(`${routePoint.address_street} ${routePoint.address_house_number}`);
    } else {
      parts.push(routePoint.address_street);
    }
  }

  // Add suburb/neighbourhood if different from city
  if (routePoint.address_suburb && routePoint.address_suburb !== routePoint.address_city) {
    parts.push(routePoint.address_suburb);
  }

  // Add city
  if (routePoint.address_city) {
    parts.push(routePoint.address_city);
  }

  // Add country
  if (routePoint.address_country) {
    parts.push(routePoint.address_country);
  }

  // If we have structured parts, join them
  if (parts.length > 0) {
    return parts.join(', ');
  }

  // Fallback to display name
  return routePoint.address_display_name || 'Unbekannte Adresse';
};
